import { PropType, reactive, ref } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import { USER_TYPE } from '@/typings/enums/USER_TYPE';
import MailApiAgency from '@/api/MailApiAgency';
import { useValidation } from '@orion.ui/orion';
import useOrganizationUserService from '@/services/organization-user/OrganizationUserService';
import { ARMADO_URL } from '@/utils/CONST';
import UserApiAgency from '@/api/UserApiAgency';

type Props = SetupProps<typeof ModalEmailSetupService.props>


export default class ModalEmailSetupService extends BaseSetupService<Props> {
	static readonly props = {
		sendToUserType: {
			type: Number,
			default: null,
			required: true,
		},
		email: {
			type: Object as PropType<Armado.Email>,
			default: null,
		},
		recipients: {
			type: Array as PropType<Armado.Recipients[]>,
			default: () => [],
		},
		subject: {
			type: String,
			default: null,
		},
		body: {
			type: String,
			default: null,
		},
		documentType: {
			type: Number,
			default: null,
			required: true,
		},
		documentIds: {
			type: Array as PropType<number[]>,
			default: () => [],
		},
		resolve: {
			type: Function,
			default: null,
		},
	};

	readonly _modal?: OrionModal;

	private readonly state = reactive({
		dataLoaded: false,
		bccOptions: [] as Armado.Recipients[],
		research: '',
		recipientsOptions: [] as Armado.Recipients[],
	});

	email = reactive({
		recipients: [] as Armado.Recipients[],
		bccRecipients: [] as Armado.Recipients[],
		subject: '',
		body: '',
	});

	validationRules = reactive({
		recipients: () => this.email.recipients.length > 0,
		subject: 'required',
		body: 'required',
	});

	get recipientsOptions () { return this.state.recipientsOptions; }
	get dataLoaded () { return this.state.dataLoaded;}
	get bccOptions () { return this.state.bccOptions; }
	get addSelfInBcc () { return this.email.bccRecipients.mapKey('id').includes(this.self.id); }

	get fetchUrl () {
		return `${ARMADO_URL}/app/organizationuser/grid}`;
	}

	validation = useValidation(this.email, this.validationRules);
	readonly _selectBcc = ref<OrionSelect>();

	constructor (props: Props, _modal?: OrionModal) {
		super(props);
		this._modal = _modal;
	}

	protected onMounted () {
		Object.assign(this.email, this.props.email);
		this.state.recipientsOptions.push(...this.props.email.recipients);
		this.state.dataLoaded = true;
	}

	protected async onBeforeUnmountAsync () {
		const { data } = await UserApiAgency.listForEmail({
			page: {
				index: 1,
				size: 20,
			},
			order: {
				property: 'User.FirstName',
				ascending: true,
			},
		}).then();

		this.state.bccOptions = data.list.map((x) => {
			return {
				id: x.id,
				label: x.firstName + ' ' + x.lastName,
			};
		});
	}

	cancel () {
		this._modal?.close();
	}

	toggleSelfInBcc () {
		this.state.addSelfInBcc = !this.state.addSelfInBcc;
		if (this.state.addSelfInBcc) {
			this.email.bccRecipients.push({
				id: this.self.id,
				label: this.self.name,
			});
		} else {
			this.email.bccRecipients = this.email.bccRecipients.filter(x => x.id !== this.self.id);
		}
	}

	async sendAsync () {
		if (!this.validation.validate()) {
			this.validation.showValidationState();
			return;
		}

		try {
			this._modal?._loader()?.show(`Envoi de votre email...`);

			if (this.self.isAgency()) {
				if (this.props.email.sendToUserType === USER_TYPE.Client) {
					await MailApiAgency.sendToClients(
						this.email.recipients.map(x => x.id),
						this.email.bccRecipients.map(x => x.id),
						this.props.documentType,
						this.props.documentIds,
						this.email.subject,
						this.email.body);

				} else if (this.props.email.sendToUserType === USER_TYPE.TempWorker) {
					await MailApiAgency.sendToTempWorkers(
						this.email.recipients.map(x => x.id),
						this.email.bccRecipients.mapKey('id'),
						this.email.subject, this.email.body);
				}
			}

			this._modal?.close();
			this.props.resolve();
		} finally {
			this._modal?._loader()?.hide();
		}
	}

	async getBccRecipientsAsync () {
		return new Promise(async (resolve) => {
			const { data } = await useOrganizationUserService().gridAsync({
				page: {
					index: 1,
					size: 20,
				},
				search: this._selectBcc.value?.getSearchTerm(),
				order: {
					property: 'User.FirstName',
					ascending: true,
				},
				organizationId: this.self.organization?.id ?? 0,
				showOnlyWithoutCompany: false,
				showOnlyWithoutRole: false,
			});
			const res = data.list.map((x) => {
				return {
					id: x.user?.id,
					label: x.user?.name,
				};
			});
			resolve(res);
		});
	}
}
